import { ProfileFieldAnswerSchema } from "./profile-fields/contract";
import { ChapterSchema } from "@/lesson-chapters-contract";
import { CourseSettingsSchema } from "@/courses/settings-contract";
import { CoursePresentationSchema } from "@/courses/presentation-contract";
// src/contracts.ts
//
// Contratos de resposta do back (schemas Zod). Fonte de verdade do que o front
// aceita: o api.ts VALIDA toda resposta contra estes schemas (garantia do
// education — não confiamos no tipo, verificamos).
import { z } from "zod";
import { RoutePolicySchema } from "@/lib/route-policy";
import { DEFAULT_FAVORITE_TERMS } from "@/lib/badge-term";

export const CapabilitySchema = z.object({
  slug: z.string(),
  requiredScopes: z.array(z.string()),
  executionMode: z.enum(["query", "command"]),
  idempotencyRequired: z.boolean(),
  description: z.string(),
});
export type Capability = z.infer<typeof CapabilitySchema>;

export const SessionSchema = z.object({
  identity: z.object({
    actorId: z.string(),
    email: z.string(),
    tenantId: z.string().nullable(),
    companyId: z.string().nullable(),
    companyAdmin: z.boolean().optional(),
    role: z.enum(["mentorado", "mentor", "admin"]).nullable(),
    scopes: z.array(z.string()),
  }),
  capabilities: z.array(CapabilitySchema),
  navigation: z.array(RoutePolicySchema).optional(),
  // Feature flags EFETIVAS do sujeito (o back computa por org/papel/usuário). O
  // front gateia a UI por aqui, junto das capabilities (anti-botão-falso).
  features: z.record(z.string(), z.boolean()).default({}),
  // Cadastro do titular (só em sessão do navegador). Mentorado sem CPF é
  // mandado para /complete-profile antes de qualquer rota do app.
  profile: z.object({ cpfRequired: z.boolean(), cpfMasked: z.string().nullable() }).optional(),
});
export type Session = z.infer<typeof SessionSchema>;

// --- Comunidade: espaços ---
export const SpaceSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  description: z.string(),
  kind: z.string(), // community | course | system
  visibility: z.string(), // public | members | private
  icon: z.string().nullable(),
  // Capa do card do espaço na Vitrine.
  coverUrl: z.string().nullable().default(null),
  position: z.number(),
  parentId: z.string().nullable(),
  freeSpace: z.boolean().default(false),
  // Só moderadores do espaço (e gestores) publicam; isModerator vem na leitura do espaço.
  onlyModeratorsCanPost: z.boolean().default(false),
  memberCount: z.number(),
  isMember: z.boolean(),
  isModerator: z.boolean().default(false),
  createdAt: z.string(),
});
export type Space = z.infer<typeof SpaceSchema>;

export const SpaceMemberSchema = z.object({
  userId: z.string(),
  role: z.string(),
  createdAt: z.string(),
  // Validade do vínculo — o Selo (back#130). null = sem validade.
  expiresAt: z.string().nullable().default(null),
});
export type SpaceMember = z.infer<typeof SpaceMemberSchema>;

export const EngagementSummarySchema = z.object({
  reactions: z.number(),
  comments: z.number(),
  reactedByMe: z.boolean(),
  savedByMe: z.boolean(),
});
export type EngagementSummary = z.infer<typeof EngagementSummarySchema>;

// --- Social: posts / feed ---
export const PostSchema = z.object({
  engagement: EngagementSummarySchema.optional(),
  id: z.string(),
  spaceId: z.string(),
  authorUserId: z.string(),
  body: z.string(),
  bodyFormat: z.enum(["plain", "markdown"]).optional(),
  media: z.unknown(),
  category: z.string(), // comunicado | documento | evento | inspiracao | geral
  pinned: z.boolean(),
  status: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Post = z.infer<typeof PostSchema>;

export const FeedPageSchema = z.object({
  items: PostSchema.array(),
  nextCursor: z.string().nullable(),
});
export type FeedPage = z.infer<typeof FeedPageSchema>;

// --- Social: engajamento ---
export const CommentSchema = z.object({
  // Ausentes em backends anteriores: não inventar o estado inicial da reação.
  reactions: z.number().optional(),
  reactedByMe: z.boolean().optional(),
  id: z.string(),
  postId: z.string(),
  authorUserId: z.string(),
  body: z.string(),
  parentId: z.string().nullable(),
  status: z.string(),
  createdAt: z.string(),
});
export type Comment = z.infer<typeof CommentSchema>;

export const ReactionResultSchema = z.object({
  reactions: z.number(),
  reactedByMe: z.boolean(),
});
export type ReactionResult = z.infer<typeof ReactionResultSchema>;

export const SaveResultSchema = z.object({ savedByMe: z.boolean(), savedCount: z.number() });
export type SaveResult = z.infer<typeof SaveResultSchema>;

// --- Social: grafo (seguir) + diretório ---
// Selo de ACESSO exibido ao lado da pessoa (back#175, fatia 7b). Nome
// `accessBadge` para não confundir com o "selo" da gamificação.
export const ShownAccessBadgeSchema = z.object({
  id: z.string(),
  name: z.string(),
  imageUrl: z.string().nullable(),
});
export type ShownAccessBadge = z.infer<typeof ShownAccessBadgeSchema>;

export const UserSearchResultSchema = z.object({
  userId: z.string(),
  name: z.string(),
  email: z.string(),
  imageUrl: z.string().nullable().optional(),
  // Só no lookup em lote (?ids=): o Selo do avatar; null = nenhum ou a marca
  // não exibe. A busca por nome não manda.
  accessBadge: ShownAccessBadgeSchema.nullable().optional(),
});
export type UserSearchResult = z.infer<typeof UserSearchResultSchema>;

export const CommunityProfileSchema = z.object({
  bio: z.string().max(320),
  coverImageUrl: z.string().nullable(),
  instagramUrl: z.string().nullable(),
  linkedinUrl: z.string().nullable(),
});
export type CommunityProfile = z.infer<typeof CommunityProfileSchema>;

export const UserProfileSchema = z.object({
  customFields: ProfileFieldAnswerSchema.array().catch([]).optional(),
  communityProfile: CommunityProfileSchema.optional(),
  userId: z.string(),
  name: z.string(),
  email: z.string(),
  imageUrl: z.string().nullable().optional(),
  followers: z.number(),
  following: z.number(),
  isFollowedByMe: z.boolean(),
  // O Selo exibido (displayed) + os "sempre visível"; [] com a chave desligada.
  accessBadges: z.array(ShownAccessBadgeSchema.extend({ displayed: z.boolean() })).default([]),
});
export type UserProfile = z.infer<typeof UserProfileSchema>;

// --- Social: notificações ---
export const NotificationSchema = z.object({
  id: z.string(),
  type: z.string(), // comment | reaction | mention | follow | message
  payload: z.unknown(),
  read: z.boolean(),
  createdAt: z.string(),
});
export type Notification = z.infer<typeof NotificationSchema>;

export const NotificationListSchema = z.object({
  items: NotificationSchema.array(),
  unread: z.number(),
});
export type NotificationList = z.infer<typeof NotificationListSchema>;

export const UnreadCountSchema = z.object({ unread: z.number() });
export type UnreadCount = z.infer<typeof UnreadCountSchema>;

// --- Social: mensagens diretas (chat) ---
export const DmThreadSchema = z.object({
  id: z.string(),
  otherUserId: z.string(),
  lastMessageAt: z.string(),
  unread: z.number(),
});
export type DmThread = z.infer<typeof DmThreadSchema>;

export const DmMessageSchema = z.object({
  id: z.string(),
  senderUserId: z.string(),
  body: z.string(),
  read: z.boolean(),
  createdAt: z.string(),
});
export type DmMessage = z.infer<typeof DmMessageSchema>;

export const DmSendResultSchema = z.object({
  threadId: z.string(),
  messageId: z.string(),
});
export type DmSendResult = z.infer<typeof DmSendResultSchema>;

// --- Vitrine (showcase) ---
export const LessonBriefSchema = z.object({
  id: z.string(),
  title: z.string(),
  durationSeconds: z.number(),
  thumbnailUrl: z.string().nullable(),
  order: z.number(),
});
export type LessonBrief = z.infer<typeof LessonBriefSchema>;

export const TrackSchema = z.object({
  programId: z.string(),
  name: z.string(),
  description: z.string(),
  spaceId: z.string().nullable().default(null),
  spaceName: z.string().nullable().default(null),
  lessonCount: z.number(),
  lessons: LessonBriefSchema.array(),
});
export type Track = z.infer<typeof TrackSchema>;

export const ContinueLessonSchema = LessonBriefSchema.extend({
  lastPositionSeconds: z.number(),
  percentComplete: z.number(),
});
export type ContinueLesson = z.infer<typeof ContinueLessonSchema>;

// Card uniforme de uma seção configurada da vitrine.
export const ShowcaseCardSchema = z.object({
  id: z.string(),
  title: z.string(),
  subtitle: z.string().nullable().default(null),
  imageUrl: z.string().nullable().default(null),
  href: z.string().nullable().default(null),
});
export type ShowcaseCard = z.infer<typeof ShowcaseCardSchema>;

export const HeroSlideSchema = z.object({
  imageUrl: z.string().optional(),
  eyebrow: z.string().optional(),
  title: z.string().optional(),
  subtitle: z.string().optional(),
  ctaLabel: z.string().optional(),
  ctaUrl: z.string().optional(),
  overlayOpacity: z.number().min(0).max(1).optional(),
  imageFormat: z.enum(["wide", "rounded", "square", "portrait"]).optional(),
});
export type HeroSlide = z.infer<typeof HeroSlideSchema>;

export const ShowcaseAppearanceSchema = z.object({
  colorScheme: z.enum(["light", "dark", "system"]).optional(),
  backgroundColor: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
  cardBackgroundColor: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
  cardBorderColor: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
  sectionTitleColor: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
  sectionTitleTransform: z.enum(["none", "uppercase"]).optional(),
  heroOverlayOpacity: z.number().min(0).max(1).optional(),
});
export type ShowcaseAppearance = z.infer<typeof ShowcaseAppearanceSchema>;

// Seção resolvida (o read-model já preencheu items/slides).
export const ResolvedSectionSchema = z.object({
  id: z.string(),
  type: z.string(),
  title: z.string().nullable().default(null),
  items: ShowcaseCardSchema.array().optional(),
  slides: HeroSlideSchema.array().optional(),
  // Corpo do bloco `markdown`. Chega do read-model como TEXTO — quem desenha é
  // o `react-markdown` de `lib/markdown.tsx`, sem `rehype-raw`, então HTML cru
  // aqui dentro é inerte.
  content: z.string().optional(),
  // Só o que o renderizador usa hoje. Back sem o campo (ou valor novo) cai no
  // card horizontal, em vez de derrubar a vitrine.
  layout: z
    .object({ cardFormat: z.enum(["landscape", "portrait"]).catch("landscape") })
    .partial()
    .optional()
    .catch(undefined),
});
export type ResolvedSection = z.infer<typeof ResolvedSectionSchema>;

// Vitrine: com config → sections (na ordem); sem config → tracks (derivado).
export const ShowcaseSchema = z.object({
  configured: z.boolean().default(false),
  sections: ResolvedSectionSchema.array().optional(),
  tracks: TrackSchema.array().optional().default([]),
  appearance: ShowcaseAppearanceSchema.default({}),
  continueWatching: ContinueLessonSchema.array(),
});
export type Showcase = z.infer<typeof ShowcaseSchema>;

export const PreviewModuleSchema = z.object({
  id: z.string(),
  title: z.string(),
  order: z.number(),
  lessons: LessonBriefSchema.array(),
});
export type PreviewModule = z.infer<typeof PreviewModuleSchema>;

export const ProgramPreviewSchema = z.object({
  program: z.object({ id: z.string(), name: z.string(), description: z.string() }).nullable(),
  lessons: LessonBriefSchema.array(),
  // Módulos hidratados da última versão publicada; [] cai no `lessons` plano.
  modules: PreviewModuleSchema.array().default([]),
});
export type ProgramPreview = z.infer<typeof ProgramPreviewSchema>;

// Tela do curso do aluno: curso ativo + módulos→aulas publicadas e a 1ª aula.
export const CoursePreviewSchema = z.object({
  course: z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    coverUrl: z.string().nullable(),
  }),
  modules: PreviewModuleSchema.array(),
  firstLessonId: z.string().nullable(),
});
export type CoursePreview = z.infer<typeof CoursePreviewSchema>;

// --- Gamificação ---
export const RankingEntrySchema = z.object({
  userId: z.string(),
  name: z.string(),
  score: z.number(),
  posts: z.number(),
  comments: z.number(),
  reactions: z.number(),
});
export type RankingEntry = z.infer<typeof RankingEntrySchema>;

export const UserBadgeSchema = z.object({
  badgeKey: z.string(),
  awardedAt: z.string(),
});
export const LearningStreakSchema = z.object({
  today: z.string(),
  timeZone: z.string(),
  currentDays: z.number().int().nonnegative(),
  longestDays: z.number().int().nonnegative(),
  completedToday: z.boolean(),
  lastCompletedDate: z.string().nullable(),
  nextMilestone: z.number().int().positive(),
  week: z.array(z.object({ date: z.string(), completed: z.boolean() })).length(7),
});
export type LearningStreak = z.infer<typeof LearningStreakSchema>;

export const MyGamificationSchema = z.object({
  userId: z.string(),
  score: z.number(),
  streakDays: z.number(),
  // Opcional durante a publicação coordenada de back e front.
  learningStreak: LearningStreakSchema.optional(),
  badges: UserBadgeSchema.array(),
});
export type MyGamification = z.infer<typeof MyGamificationSchema>;

export const BadgeSchema = z.object({
  key: z.string(),
  name: z.string(),
  description: z.string(),
});
export type Badge = z.infer<typeof BadgeSchema>;

export const StreakResultSchema = z.object({ streakDays: z.number() });
export type StreakResult = z.infer<typeof StreakResultSchema>;

// --- Admin: Funcionalidades (feature flags) ---
export const FeatureFlagOverrideSchema = z.object({
  id: z.string(),
  subjectType: z.string(),
  subjectId: z.string(),
  enabled: z.boolean(),
});
// Catálogo de funções (back#104 fase 3): o back passa a listar toda função do
// catálogo, mesmo a que a marca nunca configurou (`configured: false`, sem
// `updatedAt`). Os campos novos são opcionais: o front sobe antes do back.
export const FeatureFlagSchema = z.object({
  key: z.string(),
  label: z.string().optional(),
  enabled: z.boolean(),
  description: z.string(),
  config: z.unknown(),
  updatedAt: z.string().nullable(),
  configured: z.boolean().optional(),
  overrides: FeatureFlagOverrideSchema.array(),
});
export type FeatureFlag = z.infer<typeof FeatureFlagSchema>;

// --- Empresa e marcas acessíveis ---
export const PlatformOrganizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  createdAt: z.string(),
  companyId: z.string(),
  deletedAt: z.string().nullable().optional(),
  erasure: z
    .object({ state: z.enum(["scheduled", "erasing", "completed"]), scheduledFor: z.string() })
    .nullable()
    .optional(),
});
export type PlatformOrganization = z.infer<typeof PlatformOrganizationSchema>;

export const CompanySchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum(["unica", "grupo"]),
  createdAt: z.string(),
  brandCount: z.number(),
  canManage: z.boolean(),
  canManageLifecycle: z.boolean().optional(),
  canCreateBrand: z.boolean(),
});
export type Company = z.infer<typeof CompanySchema>;
export const CompanyContextSchema = z.object({
  company: CompanySchema,
  brands: PlatformOrganizationSchema.array(),
  administrators: z.array(
    z.object({
      userId: z.string(),
      name: z.string(),
      email: z.string(),
      createdAt: z.string(),
      isOwner: z.boolean().optional(),
    }),
  ),
});
export type CompanyContext = z.infer<typeof CompanyContextSchema>;

// --- Admin: Moderação + Domínios ---
export const ModerationActionSchema = z.object({
  moderatorUserId: z.string().optional(),
  targetType: z.string(),
  targetId: z.string(),
  action: z.string(),
  // Motivo é opcional na UI (suspender permite sem motivo; restaurar nunca envia),
  // então o back pode devolver null — o schema precisa aceitar (senão o parse quebra).
  reason: z.string().nullable(),
  createdAt: z.string(),
});
export type ModerationAction = z.infer<typeof ModerationActionSchema>;

export const AuditEventSchema = z.object({
  id: z.string(),
  actorUserId: z.string(),
  actorName: z.string().nullable(),
  actorEmail: z.string().nullable(),
  actorType: z.enum(["user", "system", "integration"]),
  actionType: z.string(),
  resourceType: z.string(),
  resourceId: z.string(),
  reason: z.string(),
  status: z.string().optional(),
  occurredAt: z.string(),
});
export type AuditEvent = z.infer<typeof AuditEventSchema>;

export const AuditPageSchema = z.object({
  items: z.array(AuditEventSchema),
  pageInfo: z.object({
    nextCursor: z.string().nullable(),
    hasNextPage: z.boolean(),
    total: z.number(),
  }),
  summary: z.object({
    totalEvents: z.number(),
    totalSessions: z.number(),
    totalMinutes: z.number(),
    pendingAnomalies: z.number(),
    noShows: z.number(),
  }),
});
export type AuditPage = z.infer<typeof AuditPageSchema>;

export const AuditReviewSchema = z.object({
  id: z.string(),
  eventId: z.string(),
  decision: z.enum(["approved", "rejected"]),
  reason: z.string(),
});
export type AuditReview = z.infer<typeof AuditReviewSchema>;

export const DomainCheckSchema = z.object({
  allowed: z.boolean(),
  domain: z.string().nullable(),
});
export type DomainCheck = z.infer<typeof DomainCheckSchema>;

// --- Admin: Insights ---
export const InsightDefinitionSchema = z.object({
  kind: z.string(),
  key: z.string(),
  name: z.string(),
  definition: z.record(z.string(), z.unknown()),
});
export type InsightDefinition = z.infer<typeof InsightDefinitionSchema>;

// --- Admin: Integrações ---
// Escopo concedível a uma chave. Vem do back para a tela não hardcodar strings:
// um escopo com typo cria uma chave que parece válida e nunca funciona.
export const ApiScopeSchema = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string(),
});
export type ApiScope = z.infer<typeof ApiScopeSchema>;

export const ApiKeySchema = z.object({
  id: z.string(),
  label: z.string(),
  keyPrefix: z.string(),
  revoked: z.boolean(),
  scopes: z.array(z.string()),
  createdAt: z.string(),
});
export type ApiKey = z.infer<typeof ApiKeySchema>;

export const ApiKeyCreatedSchema = z.object({
  id: z.string(),
  label: z.string(),
  secret: z.string().nullable(),
  keyPrefix: z.string(),
});
export type ApiKeyCreated = z.infer<typeof ApiKeyCreatedSchema>;

// Chaves do servidor MCP (mcp_sk_…). Datas chegam como ISO string (ou null).
export const McpKeySchema = z.object({
  subjectName: z.string().nullable().optional(),
  issuerName: z.string().nullable().optional(),
  readOnly: z.boolean(),
  id: z.string(),
  name: z.string(),
  userId: z.string(),
  keyPrefix: z.string(),
  lastUsedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
});
export type McpKey = z.infer<typeof McpKeySchema>;

// Na emissão o segredo cru vem UMA vez (não é recuperável depois).
export const McpKeyCreatedSchema = McpKeySchema.extend({
  secret: z.string().optional(),
});
export type McpKeyCreated = z.infer<typeof McpKeyCreatedSchema>;

export const OutboundWebhookSchema = z.object({
  id: z.string(),
  url: z.string(),
  events: z.unknown(),
  // Recorte (back#178, W1): { accessBadgeId } = só os eventos daquele Selo.
  filters: z.object({ accessBadgeId: z.string().optional() }).default({}),
  active: z.boolean(),
});
export type OutboundWebhook = z.infer<typeof OutboundWebhookSchema>;

// Entrega de um webhook (back#187): uma por (webhook, evento), com tentativas,
// próxima tentativa (backoff), status HTTP e erro.
export const WebhookDeliverySchema = z.object({
  id: z.string(),
  eventId: z.string(),
  eventType: z.string(),
  status: z.enum(["pending", "sent", "error", "dead"]),
  attempts: z.number(),
  nextAttemptAt: z.string().nullable(),
  responseStatus: z.number().nullable(),
  lastError: z.string().nullable(),
  createdAt: z.string(),
  sentAt: z.string().nullable(),
});
export type WebhookDelivery = z.infer<typeof WebhookDeliverySchema>;

export const AutomationSchema = z.object({
  id: z.string(),
  name: z.string(),
  trigger: z.string(),
  enabled: z.boolean(),
});
export type Automation = z.infer<typeof AutomationSchema>;

export const ZoomStatusSchema = z.object({ configured: z.boolean(), note: z.string() });
export type ZoomStatus = z.infer<typeof ZoomStatusSchema>;

// --- Admin: Institucional (páginas, templates, menu mobile) ---
// Onde a página aparece (escolhido pelo admin). Espelha PageNavBody do back;
// todos os campos opcionais, default {} quando a página ainda não tem placement.
export const PageNavSchema = z.object({
  footer: z.boolean().optional(),
  sidebar: z.boolean().optional(),
  mobile: z.boolean().optional(),
  order: z.number().optional(),
  icon: z.string().optional(),
});
export type PageNav = z.infer<typeof PageNavSchema>;

export const PageListItemSchema = z.object({
  slug: z.string(),
  title: z.string(),
  published: z.boolean(),
  nav: PageNavSchema.default({}),
});
export type PageListItem = z.infer<typeof PageListItemSchema>;

export const PageDetailSchema = z.object({
  slug: z.string(),
  title: z.string(),
  body: z.string(),
  published: z.boolean(),
  nav: PageNavSchema.default({}),
});
export type PageDetail = z.infer<typeof PageDetailSchema>;

export const EmailTemplateListItemSchema = z.object({ key: z.string(), subject: z.string() });
export type EmailTemplateListItem = z.infer<typeof EmailTemplateListItemSchema>;

export const RenderedTemplateSchema = z.object({ subject: z.string(), body: z.string() });
export type RenderedTemplate = z.infer<typeof RenderedTemplateSchema>;

// Item da barra inferior do celular. `target` é uma rota do app (/feed) ou uma
// página institucional (/p/<slug>). Tolera itens legados que usavam `slug`
// (normaliza para target) para não quebrar a leitura de menus antigos.
export const MobileMenuItemSchema = z
  .object({
    label: z.string(),
    target: z.string().optional(),
    slug: z.string().optional(),
    icon: z.string().optional(),
    order: z.number().optional(),
  })
  .transform((it) => ({
    label: it.label,
    target: it.target ?? (it.slug ? `/p/${it.slug}` : "/"),
    icon: it.icon,
    order: it.order,
  }));
export type MobileMenuItem = z.infer<typeof MobileMenuItemSchema>;

export const MobileMenuSchema = z.object({ items: z.array(MobileMenuItemSchema) });
export type MobileMenu = z.infer<typeof MobileMenuSchema>;

// --- Admin: Personalizar (white-label) ---
// Basic info / whitelabel tipado (espelha BrandingSettingsBody do back).
// Prazo favorito: meses OU dias (trial de 30 dias, back#171), rótulo opcional.
export const FavoriteTermSchema = z.union([
  z.object({ months: z.number(), label: z.string().optional() }),
  z.object({ days: z.number(), label: z.string().optional() }),
]);

export const BrandingSettingsSchema = z.object({
  displayName: z.string().optional(),
  openingPage: z.enum(["showcase", "feed", "painel"]).optional(),
  showcaseSpaceId: z.string().nullable().optional(),
  // "Selos no perfil" (back#175): desligada = nenhum Selo aparece para os membros.
  showAccessBadges: z.boolean().optional(),
  showLessonLikeCounts: z.boolean().optional(),
  // Nome do assistente de IA do player; vazio = "Mentor IA".
  assistantName: z.string().optional(),
  // Foto do assistente (back#595), no card e na conversa; null/ausente = avatar padrão.
  assistantAvatarUrl: z.string().nullable().optional(),
  redirectUrlNoAccess: z.string().optional(),
  communityPrivacy: z.boolean().optional(),
  darkModeDefault: z.boolean().optional(),
  defaultTheme: z.enum(["light", "dark", "startup"]).optional(),
  supportUrl: z.string().optional(),
  social: z
    .object({ instagram: z.string().optional(), facebook: z.string().optional() })
    .optional(),
  // Banner hero personalizável por tenant (Visão geral + Vitrine). Espelha o
  // BrandingSettingsBody do back. Estado honesto: só renderiza quando enabled.
  heroBanner: z
    .object({
      enabled: z.boolean().optional(),
      imageUrl: z.string().nullable().optional(),
      title: z.string().optional(),
      subtitle: z.string().optional(),
      ctaLabel: z.string().optional(),
      ctaUrl: z.string().optional(),
    })
    .optional(),
  // Prazos favoritos para dar Selo (back#143). O back devolve o efetivo (6 e
  // 12 de fábrica); ausente = back anterior, sem atalhos.
  // Prazos favoritos: meses OU dias (trial de 30 dias, back#171).
  badgeTerms: z.array(FavoriteTermSchema).optional(),
});
export type BrandingSettings = z.infer<typeof BrandingSettingsSchema>;

export const BrandingSchema = z.object({
  colors: z.record(z.string(), z.unknown()),
  images: z.record(z.string(), z.unknown()),
  texts: z.record(z.string(), z.unknown()),
  logo: z.string().nullable(),
  // Default do back é {} — settings pode vir ausente em respostas antigas.
  settings: BrandingSettingsSchema.default({}),
});
export type Branding = z.infer<typeof BrandingSchema>;

// Subdomínio da plataforma da marca (#350). `base` null = recurso desligado no
// ambiente. Só `active` foi verificado no próprio endereço e está publicado.
export const BrandSubdomainSchema = z.object({
  base: z.string().nullable(),
  subdomain: z.string().nullable(),
  host: z.string().nullable(),
  origin: z.string().nullable(),
  status: z.enum(["pending", "active", "error"]).nullable(),
  error: z.string().nullable(),
  checkedAt: z.string().nullable(),
});
export type BrandSubdomain = z.infer<typeof BrandSubdomainSchema>;

// Projeção segura para páginas públicas (login/reset). O backend resolve o
// tenant pelo host e nunca devolve settings administrativas nesta superfície.
export const PublicBrandingSchema = z.object({
  logo: z.string().nullable().default(null),
  colors: z
    .object({
      primary: z.string().optional(),
      accent: z.string().optional(),
      pwaBackground: z.string().optional(),
      loginBackground: z.string().optional(),
    })
    .default({}),
  settings: z
    .object({
      displayName: z.string().optional(),
      defaultTheme: z.enum(["light", "dark", "startup"]).optional(),
    })
    .default({}),
  texts: z
    .object({
      welcomeTitle: z.string().optional(),
      loginSubtitle: z.string().optional(),
      tabTitle: z.string().optional(),
      pwaName: z.string().optional(),
      pwaShortName: z.string().optional(),
    })
    .default({}),
  images: z
    .object({
      loginBanner: z.string().optional(),
      favicon: z.string().optional(),
      pwaIcon: z.string().optional(),
      logoLight: z.string().optional(),
      logoDark: z.string().optional(),
    })
    .default({}),
});
export type PublicBranding = z.infer<typeof PublicBrandingSchema>;

// --- Insights: Score dos usuários (ABH_023) ---
export const UserScoreRowSchema = z.object({
  userId: z.string(),
  name: z.string(),
  score: z.number(),
  posts: z.number(),
  comentarios: z.number(),
  reacoes: z.number(),
  aulas: z.number(),
  minAssistidos: z.number(),
  cursos: z.number(),
  logins: z.number(),
  sequencia: z.number(),
  selo: z.string().nullable(),
  coins: z.number(),
});
export type UserScoreRow = z.infer<typeof UserScoreRowSchema>;

export const UserScoreListSchema = z.object({
  rows: z.array(UserScoreRowSchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
});
export type UserScoreList = z.infer<typeof UserScoreListSchema>;

// --- Carteira de moedas (coin wallet) ---
export const WalletTxSchema = z.object({
  id: z.string(),
  amount: z.number(),
  reason: z.string(),
  createdAt: z.string(),
});
export const WalletMeSchema = z.object({
  balance: z.number(),
  transactions: WalletTxSchema.array(),
});
export type WalletMe = z.infer<typeof WalletMeSchema>;

export const WalletSummarySchema = z.object({
  totalCirculating: z.number(),
  holders: z.array(z.object({ userId: z.string(), balance: z.number() })),
});
export type WalletSummary = z.infer<typeof WalletSummarySchema>;

// --- Comentários em Aulas (ABD_003) ---
export const LessonCommentSchema = z.object({
  id: z.string(),
  lessonId: z.string(),
  authorUserId: z.string(),
  body: z.string(),
  parentId: z.string().nullable(),
  status: z.string(),
  answeredAt: z.string().nullable(),
  createdAt: z.string(),
});
export type LessonComment = z.infer<typeof LessonCommentSchema>;

export const ModerationCommentSchema = LessonCommentSchema.extend({
  lessonTitle: z.string(),
  programId: z.string().nullable(),
});
export type ModerationComment = z.infer<typeof ModerationCommentSchema>;

// --- Tags administráveis (categorias de post) ---
export const TagSchema = z.object({
  archived: z.boolean().optional(),
  position: z.number().int().nonnegative().optional(),
  id: z.string().optional(),
  key: z.string(),
  name: z.string(),
  color: z.string(),
  imageUrl: z.string().nullable().default(null),
  requiresPrivilege: z.boolean(),
  builtin: z.boolean(),
});
export type Tag = z.infer<typeof TagSchema>;

// --- Admin: Gestão de Usuários (ABD_001) ---
// Cada concessão de Selo da pessoa nesta marca (back#596). `remaining` vem
// calculado pelo servidor — o mesmo texto da exportação. Lista vazia = sem acesso.
export const AccessGrantSchema = z.object({
  badgeId: z.string(),
  badgeName: z.string(),
  grantedAt: z.string(),
  expiresAt: z.string().nullable(),
  source: z.string(),
  // removed: o Selo foi retirado; o último ciclo vem do histórico do servidor.
  situation: z.enum(["valid", "expired", "removed"]),
  remaining: z.string(),
  removedAt: z.string().nullable().default(null),
});
export type AccessGrant = z.infer<typeof AccessGrantSchema>;

export const AdminUserSchema = z.object({
  userId: z.string(),
  name: z.string(),
  username: z.string(),
  email: z.string(),
  emailConfirmed: z.boolean(),
  status: z.enum(["active", "suspended"]),
  role: z.string(),
  // Default false: back anterior a este campo não o envia.
  companyAdmin: z.boolean().default(false),
  createdAt: z.string(),
  lastLoginAt: z.string().nullable(),
  coins: z.number(),
  // Selo da GAMIFICAÇÃO mais recente (nome).
  badge: z.string().nullable(),
  // Selo de ACESSO exibido (back#175, B2); a gestão vê mesmo com a chave desligada.
  accessBadge: ShownAccessBadgeSchema.nullable().default(null),
  // Só a máscara (***.456.789-**); back anterior não envia.
  cpfMasked: z.string().nullable().default(null),
  // back#596; back anterior não envia.
  accessGrants: z.array(AccessGrantSchema).default([]),
});
export type AdminUser = z.infer<typeof AdminUserSchema>;

export const MetricsExclusionSchema = z.object({
  reason: z.string(),
  excludedAt: z.string(),
  excludedBy: z.string().nullable(),
});
export const UserDiagnosisSummarySchema = z.object({
  answered: z.boolean(),
  sentAt: z.string().nullable(),
  answeredAt: z.string().nullable(),
  resultReady: z.boolean(),
});
export const AdminUserDetailSchema = AdminUserSchema.extend({
  capabilities: z.array(CapabilitySchema),
  metricsExclusion: MetricsExclusionSchema.nullable().optional(),
  diagnosisSummary: UserDiagnosisSummarySchema.nullable().optional(),
});
export type AdminUserDetail = z.infer<typeof AdminUserDetailSchema>;

// Primeiro acesso dos mentorados da marca, para o resumo no topo de Usuários.
export const ActivationSummarySchema = z.object({
  mentees: z.number(),
  accessed: z.number(),
  profileCompleted: z.number(),
  diagnosed: z.number(),
  firstLesson: z.number(),
});
export type ActivationSummary = z.infer<typeof ActivationSummarySchema>;

export const AdminUserListSchema = z.object({
  rows: z.array(AdminUserSchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
  // Opcional: o back anterior não envia o resumo.
  activation: ActivationSummarySchema.optional(),
});
export type AdminUserList = z.infer<typeof AdminUserListSchema>;

// Vínculos de espaço (Selos) gravados pelo import (back#132). `mantidos`: o
// aluno já tinha validade MAIOR que a planilha — fica a do banco, e a lista
// diz a quem estender. Datas em ISO (UTC).
export const ImportGrantSummarySchema = z.object({
  criados: z.number(),
  estendidos: z.number(),
  iguais: z.number(),
  mantidos: z.array(
    z.object({
      linha: z.number(),
      email: z.string(),
      espaco: z.string(),
      validadeAtual: z.string().nullable(),
      validadePlanilha: z.string().nullable(),
    }),
  ),
});

export const ImportResultSchema = z.object({
  criados: z.number(),
  ignorados: z.number(),
  erros: z.array(z.string()),
  // Campos da back#132. Um back anterior não os envia: os padrões mantêm a
  // tela funcionando com os dois (front e back sobem separados).
  avisos: z.array(z.string()).default([]),
  vinculos: ImportGrantSummarySchema.default({
    criados: 0,
    estendidos: 0,
    iguais: 0,
    mantidos: [],
  }),
  // Selos de acesso concedidos pela coluna `badge` (back#151). Mesmo formato dos
  // vínculos; em `mantidos`, `espaco` traz o nome do Selo.
  selos: ImportGrantSummarySchema.default({ criados: 0, estendidos: 0, iguais: 0, mantidos: [] }),
  convites: z
    .object({ enviados: z.number(), falhas: z.array(z.string()) })
    .default({ enviados: 0, falhas: [] }),
});
export type ImportResult = z.infer<typeof ImportResultSchema>;

// Retorno do PATCH de usuário (parcial: só o que mudou).
export const AdminUserUpdateSchema = z.object({
  userId: z.string(),
  role: z.string().optional(),
  status: z.string().optional(),
});
export type AdminUserUpdate = z.infer<typeof AdminUserUpdateSchema>;

export const AdminUserRemovalSchema = z.object({
  userId: z.string(),
  removed: z.literal(true),
});
export type AdminUserRemoval = z.infer<typeof AdminUserRemovalSchema>;
// Senha temporária gerada pelo Gestor (back: POST …/temporary-password).
export const AdminUserTempPasswordSchema = z.object({
  userId: z.string(),
  tempPassword: z.string(),
});

// --- Admin: Publicidades ---
export const AdSchema = z.object({
  showOnLogin: z.boolean().optional(),
  description: z.string().optional(),
  mediaType: z.enum(["image", "video"]).optional(),
  id: z.string(),
  title: z.string(),
  mediaUrl: z.string(),
  linkUrl: z.string(),
  placement: z.string(),
  active: z.boolean(),
  weight: z.number(),
  startsAt: z.string().nullable(),
  endsAt: z.string().nullable(),
});
export type Ad = z.infer<typeof AdSchema>;

// --- Painel admin: série de atividade (charts) ---
export const ActivityPointSchema = z.object({
  date: z.string(),
  cadastros: z.number(),
  logins: z.number(),
  posts: z.number(),
  comentarios: z.number(),
  curtidas: z.number(),
});
export type ActivityPoint = z.infer<typeof ActivityPointSchema>;

// --- Painel admin: dashboard social ---
export const AdminDashboardSchema = z.object({
  users: z.object({
    total: z.number(),
    connectedNow: z.number(),
    activeInPeriod: z.number(),
    activePct: z.number().default(0),
  }),
  engagement: z.object({
    posts: z.number(),
    comments: z.number(),
    reactions: z.number(),
    messages: z.number(),
  }),
  periodDays: z.number(),
});
export type AdminDashboard = z.infer<typeof AdminDashboardSchema>;

// Abas do dashboard (Posts).

export const DashboardPostSchema = z.object({
  postId: z.string(),
  authorUserId: z.string(),
  excerpt: z.string(),
  category: z.string(),
  likes: z.number(),
  comments: z.number(),
  createdAt: z.string(),
});
export type DashboardPost = z.infer<typeof DashboardPostSchema>;

// --- Gestão ---
// Hero da página-vitrine do programa (definível pelo admin).
export const ProgramHeroSchema = z.object({
  enabled: z.boolean().optional(),
  imageUrl: z.string().optional(),
  title: z.string().optional(),
  subtitle: z.string().optional(),
  ctaLabel: z.string().optional(),
  ctaUrl: z.string().optional(),
});
export type ProgramHero = z.infer<typeof ProgramHeroSchema>;

export const ProgramSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  status: z.string(),
  spaceId: z.string().nullable().default(null),
  hero: ProgramHeroSchema.default({}),
  // Capa do card da trilha (vitrine e catálogo); o hero fica para o topo da página.
  coverUrl: z.string().nullable().default(null),
  createdAt: z.string(),
  createdBy: z.string(),
  version: z.number(),
});

// Vitrine do programa: hero + cursos como cards (ShowcaseCard).
export const ProgramVitrineSchema = z.object({
  program: z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    hero: ProgramHeroSchema.default({}),
  }),
  courses: ShowcaseCardSchema.array(),
});
export type ProgramVitrine = z.infer<typeof ProgramVitrineSchema>;
export type Program = z.infer<typeof ProgramSchema>;

// Curso — a unidade de currículo. programId nulo = curso avulso (fora de trilha).
export const CourseSchema = z.object({
  presentation: CoursePresentationSchema.optional(),
  settings: CourseSettingsSchema.optional(),
  id: z.string(),
  programId: z.string().nullable().default(null),
  name: z.string(),
  description: z.string(),
  status: z.string(),
  order: z.number(),
  spaceId: z.string().nullable().default(null),
  coverUrl: z.string().nullable().default(null),
  createdAt: z.string(),
  createdBy: z.string(),
  version: z.number(),
});
export type Course = z.infer<typeof CourseSchema>;

export const CohortSchema = z.object({
  id: z.string(),
  programId: z.string(),
  curriculumVersionId: z.string(),
  name: z.string(),
  startsAt: z.string(),
  endsAt: z.string(),
  status: z.string(),
});
export type Cohort = z.infer<typeof CohortSchema>;

// Estrutura tipada do currículo (espelha o back): módulos → itens (aulas).
export const CurriculumItemSchema = z.object({
  id: z.string(),
  kind: z.literal("lesson"),
  lessonId: z.string(),
  title: z.string(),
  order: z.number(),
});
export type CurriculumItem = z.infer<typeof CurriculumItemSchema>;

export const CurriculumModuleSchema = z.object({
  id: z.string(),
  title: z.string(),
  order: z.number(),
  items: z.array(CurriculumItemSchema),
});
export type CurriculumModule = z.infer<typeof CurriculumModuleSchema>;

export const CurriculumContentSchema = z.object({
  modules: z.array(CurriculumModuleSchema),
});
export type CurriculumContent = z.infer<typeof CurriculumContentSchema>;

// Serve tanto ao currículo de programa (legado) quanto ao de curso: programId
// virou nullable e courseId é o novo dono (nulo em versões antigas).
export const CurriculumVersionSchema = z.object({
  id: z.string(),
  programId: z.string().nullable().default(null),
  courseId: z.string().nullable().default(null),
  version: z.number(),
  status: z.string(),
  content: z.unknown(),
  publishedAt: z.string().nullable(),
});
export type CurriculumVersion = z.infer<typeof CurriculumVersionSchema>;

export const EnrollmentSchema = z.object({
  id: z.string(),
  cohortId: z.string(),
  learnerId: z.string(),
  userId: z.string().nullable(),
  status: z.string(),
  enrolledAt: z.string(),
  source: z.string(),
});
export type Enrollment = z.infer<typeof EnrollmentSchema>;

// --- Acesso (convites) ---
export const InviteSchema = z.object({
  roleId: z.string().nullable().optional(),
  roleName: z.string().nullable().optional(),
  roleVersion: z.number().nullable().optional(),
  id: z.string(),
  organizationId: z.string(),
  token: z.string(),
  url: z.string(),
  role: z.string(),
  cohortId: z.string().nullable(),
  email: z.string().nullable(),
  status: z.string(),
  expiresAt: z.string(),
  invitedBy: z.string(),
  acceptedUserId: z.string().nullable(),
  acceptedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type Invite = z.infer<typeof InviteSchema>;

export const CreateInviteResultSchema = InviteSchema.extend({
  // Backends anteriores não informavam entrega; nesse caso não afirmar envio.
  emailDelivery: z.enum(["sent", "failed", "not_requested", "unknown"]).default("unknown"),
});

export const InvitePublicSchema = z.object({
  valid: z.boolean(),
  organizationName: z.string().nullable(),
  cohortName: z.string().nullable(),
  role: z.string(),
  email: z.string().nullable(),
});
export type InvitePublic = z.infer<typeof InvitePublicSchema>;

export const ProvisionResultSchema = z.object({
  userId: z.string(),
  email: z.string(),
  tempPassword: z.string(),
});
export type ProvisionResult = z.infer<typeof ProvisionResultSchema>;

// --- Conteúdo ---
export const LessonSchema = z.object({
  categoryIds: z.array(z.string().uuid()).optional(),
  chapters: ChapterSchema.array().optional(),
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  mediaKind: z.string(),
  mediaProvider: z.string(),
  mediaUrl: z.string(),
  body: z.string().nullable(),
  durationSeconds: z.number(),
  thumbnailUrl: z.string().nullable(),
  order: z.number(),
  freeClass: z.boolean(),
  allowComments: z.boolean(),
  status: z.string(),
  programIds: z.array(z.string()).default([]),
  createdAt: z.string(),
});
export type Lesson = z.infer<typeof LessonSchema>;

export const LessonMaterialSchema = z.object({
  id: z.string(),
  lessonId: z.string(),
  role: z.string(),
  kind: z.string(),
  title: z.string(),
  url: z.string(),
  sizeBytes: z.number().nullable(),
  createdAt: z.string(),
  downloadCount: z.number(),
});
export type LessonMaterial = z.infer<typeof LessonMaterialSchema>;

export const MaterialDownloadStatSchema = z.object({
  learnerId: z.string(),
  count: z.number(),
  firstDownloadedAt: z.string(),
  lastDownloadedAt: z.string(),
});
export type MaterialDownloadStat = z.infer<typeof MaterialDownloadStatSchema>;

export const WatchSchema = z.object({
  id: z.string(),
  lessonId: z.string(),
  learnerId: z.string(),
  watchedSeconds: z.number(),
  percentComplete: z.number(),
  lastPositionSeconds: z.number(),
  completedAt: z.string().nullable(),
  updatedAt: z.string(),
});
export type Watch = z.infer<typeof WatchSchema>;

// --- Outcome Engine ---
export const OutcomeContractSchema = z.object({
  id: z.string(),
  subjectId: z.string(),
  problem: z.string(),
  baseline: z.number(),
  target: z.number(),
  unit: z.string(),
  metricSource: z.string(),
  acceptedEvidence: z.string(),
  deadline: z.string(),
  reviewCadence: z.string(),
  status: z.string(),
  createdAt: z.string(),
  createdBy: z.string(),
  acceptedAt: z.string().nullable(),
  // 5W2H (back#656): Por quê, Onde, Quem e Como; e a origem (ia | manual).
  // Opcionais para o back anterior.
  why: z.string().nullable().optional(),
  where: z.string().nullable().optional(),
  who: z.string().nullable().optional(),
  how: z.string().nullable().optional(),
  // Descrição do planejamento (back#714); opcional para o back anterior.
  description: z.string().nullable().optional(),
  // Pilar do diagnóstico (back#703): liga o planejamento ao mapa de maturidade.
  pillar: z
    .enum(["planning", "sales", "marketing", "leadership", "management"])
    .nullable()
    .optional(),
  source: z.string().optional(),
  // Última métrica e o caminho até a meta (back#656); opcionais.
  lastValue: z.number().nullable().optional(),
  progress: z.number().nullable().optional(),
});
export type OutcomeContract = z.infer<typeof OutcomeContractSchema>;

// Métrica registrada num contrato (back outcomes MetricObservationRow).
export const MetricObservationSchema = z.object({
  id: z.string(),
  outcomeContractId: z.string(),
  value: z.number(),
  observedAt: z.string(),
  source: z.string(),
  note: z.string().nullable(),
  createdAt: z.string(),
});
export type MetricObservation = z.infer<typeof MetricObservationSchema>;

// Toda meta tem planejamento. Descrição, evidência, critério, indicador e prazo são opcionais.
export const CommitmentSchema = z.object({
  id: z.string(),
  outcomeContractId: z.string(),
  subjectId: z.string(),
  activityId: z.string().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  evidenceRequired: z.string().nullable(),
  rubric: z.string().nullable(),
  kpiAffected: z.string().nullable(),
  deadline: z.string().nullable(),
  status: z.string(),
  version: z.number(),
  currentEvidence: z.string().nullable(),
  reviewNote: z.string().nullable(),
  reviewedBy: z.string().nullable(),
  reviewedAt: z.string().nullable(),
  createdAt: z.string(),
  createdBy: z.string().nullable().optional(),
  source: z.enum(["diagnosis", "manual"]).optional(),
  position: z.number().nullable().optional(),
  completedAt: z.string().nullable().optional(),
  editedAt: z.string().nullable().optional(),
  // Só na lista: para agrupar por pessoa.
  subjectName: z.string().nullable().optional(),
});
export type Commitment = z.infer<typeof CommitmentSchema>;

export const InterventionCaseSchema = z.object({
  id: z.string(),
  outcomeContractId: z.string(),
  subjectId: z.string(),
  trigger: z.string(),
  severity: z.string(),
  slaDeadline: z.string(),
  recommendedAction: z.string(),
  cerebroRefs: z.array(z.string()).nullable(),
  status: z.string(),
  approvedBy: z.string().nullable(),
  executedBy: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type InterventionCase = z.infer<typeof InterventionCaseSchema>;

export const ApplicableSchema = z.object({
  id: z.string(),
  lessonRef: z.string(),
  title: z.string(),
  action: z.string(),
  rubric: z.string(),
  evidenceRequired: z.string(),
  kpiAffected: z.string(),
  generation: z.string(),
  cerebroRefs: z.array(z.string()).nullable(),
  confidence: z.number().nullable(),
  status: z.string(),
  reviewedBy: z.string().nullable(),
  reviewNote: z.string().nullable(),
  publishedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type Applicable = z.infer<typeof ApplicableSchema>;

// --- Lead Twin ---
export const TwinSchema = z.object({
  id: z.string(),
  subjectId: z.string(),
  personType: z.string(),
  displayName: z.string(),
  email: z.string().nullable(),
  company: z.string().nullable(),
  role: z.string().nullable(),
  sector: z.string().nullable(),
  revenueBand: z.string().nullable(),
  version: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Twin = z.infer<typeof TwinSchema>;

// --- Helena ---
export const ProposalSchema = z.object({
  id: z.string(),
  subjectId: z.string(),
  purpose: z.string(),
  content: z.unknown(),
  risk: z.string(),
  influenceMode: z.string(),
  status: z.string(),
  createdAt: z.string(),
});
export type Proposal = z.infer<typeof ProposalSchema>;

// --- Negócio ---

const CountByStatus = z.object({ total: z.number(), byStatus: z.record(z.string(), z.number()) });
export const AnalyticsOverviewSchema = z.object({
  programs: z.number(),
  cohorts: z.number(),
  lessons: z.number(),
  mentees: z.number().int().nonnegative().optional(),
  enrollments: CountByStatus,
  outcomes: CountByStatus,
  commitments: CountByStatus,
});
export type AnalyticsOverview = z.infer<typeof AnalyticsOverviewSchema>;
// Quebra das trilhas e aulas (back /v1/admin/dashboard/courses): as mesmas
// contagens de AnalyticsOverview (todas as trilhas e aulas da marca), por estado.
export const AnalyticsCoursesSchema = z.object({
  programs: z.object({ total: z.number(), active: z.number() }),
  lessons: z.object({ total: z.number(), published: z.number(), draft: z.number() }),
});
export type AnalyticsCourses = z.infer<typeof AnalyticsCoursesSchema>;

// ── Selos de acesso (back#151/#157) ─────────────────────────────────────────
// Entidade própria: o Selo libera cursos e programas (N:N) e é concedido a cada
// aluno com validade. Não confundir com os selos da gamificação (BadgeSchema).
export const HomeLinkSchema = z.union([
  z
    .object({
      kind: z.literal("internal"),
      target: z.enum(["overview", "feed", "messages", "ranking"]),
    })
    .strict(),
  z
    .object({
      kind: z.literal("internal"),
      target: z.enum(["showcase", "courses", "spaces"]),
      id: z.string().uuid().optional(),
    })
    .strict(),
]);
export type HomeLink = z.infer<typeof HomeLinkSchema>;
export const MyHomeBadgeSchema = z.object({
  id: z.string(),
  valid: z.boolean(),
  homeLink: HomeLinkSchema.nullable().optional().default(null),
});

export const AccessBadgeSchema = z.object({
  id: z.string(),
  homeLink: HomeLinkSchema.nullable().optional().default(null),
  name: z.string(),
  description: z.string(),
  imageUrl: z.string().nullable().default(null),
  // Fatia 7 (back#175): maior relevância = o Selo exibido no avatar e no menu;
  // "sempre visível" = aparece no perfil mesmo sem ser o de maior relevância.
  relevance: z.number(),
  alwaysVisible: z.boolean(),
  // Prazos favoritos DESTE Selo (atalhos ao dar/renovar). Back anterior não
  // devolve: vale o de fábrica, o mesmo que ele aplicava.
  terms: z.array(FavoriteTermSchema).default(DEFAULT_FAVORITE_TERMS),
  // Prazo aplicado quando a integração (CRM) libera o Selo (back#381);
  // null = a integração não libera este Selo sem venda.
  accessTerm: z
    .union([z.object({ months: z.number() }), z.object({ days: z.number() })])
    .nullable()
    .default(null),
  // Arquivado (back#235, F4): não recebe mentorado novo; null = ativo.
  archivedAt: z.string().nullable().default(null),
  createdAt: z.string(),
  updatedAt: z.string(),
  // Só na listagem.
  contentCount: z.number().optional(),
  activeGrantCount: z.number().optional(),
  // Quantos exibem este Selo como o ativo (0 com a chave da marca desligada).
  displayedCount: z.number().optional(),
});
export type AccessBadge = z.infer<typeof AccessBadgeSchema>;

// Resultado da ação em massa (back#192, #177): quantos atingidos, quantos
// deram certo e quem falhou (com o código e a mensagem do back).
export const BulkActionResultSchema = z.object({
  bulkId: z.string(),
  action: z.string(),
  total: z.number(),
  ok: z.number(),
  failed: z.array(z.object({ userId: z.string(), code: z.string(), message: z.string() })),
});
export type BulkActionResult = z.infer<typeof BulkActionResultSchema>;

export const AccessBadgeDetailSchema = AccessBadgeSchema.extend({
  courseIds: z.array(z.string()),
  programIds: z.array(z.string()),
});
export type AccessBadgeDetail = z.infer<typeof AccessBadgeDetailSchema>;

export const AccessBadgeContentsSchema = z.object({
  courseIds: z.array(z.string()),
  programIds: z.array(z.string()),
});

export const AccessBadgeGrantSchema = z.object({
  userId: z.string(),
  name: z.string().nullable().default(null),
  email: z.string().nullable().default(null),
  // null = sem validade.
  expiresAt: z.string().nullable(),
  // Catálogo do back (access-badges/domain.ts): manual | import | bulk | migration.
  source: z.string(),
  // "Atribuído em": o primeiro cadastro — âncora da renovação por prazo.
  grantedAt: z.string(),
  valid: z.boolean(),
});
export type AccessBadgeGrant = z.infer<typeof AccessBadgeGrantSchema>;
// Aba Usuários (back#173): filtrada e paginada no servidor, por nome.
export const AccessBadgeGrantPageSchema = z.object({
  items: z.array(AccessBadgeGrantSchema),
  nextCursor: z.string().nullable(),
});

// Histórico das concessões (back#172): uma linha por dar/renovar/remover.
export const AccessBadgeHistoryItemSchema = z.object({
  id: z.string(),
  userId: z.string(),
  userName: z.string().nullable(),
  userEmail: z.string().nullable(),
  action: z.enum(["granted", "renewed", "revoked", "expired"]),
  source: z.string(),
  expiresAt: z.string().nullable(),
  // null = operador desconhecido (passado importado do outbox).
  actorId: z.string().nullable(),
  actorName: z.string().nullable(),
  occurredAt: z.string(),
});
export type AccessBadgeHistoryItem = z.infer<typeof AccessBadgeHistoryItemSchema>;
export const AccessBadgeHistoryPageSchema = z.object({
  items: z.array(AccessBadgeHistoryItemSchema),
  nextCursor: z.string().nullable(),
});

// Aba Origens (back#173): um lote por linha — ação em massa ou importação —
// com quando, quem e quantos alunos foram adicionados, renovados e removidos.
export const AccessBadgeOriginBatchSchema = z.object({
  batchId: z.string(),
  startedAt: z.string(),
  actorId: z.string().nullable(),
  actorName: z.string().nullable(),
  granted: z.number(),
  renewed: z.number(),
  revoked: z.number(),
});
export type AccessBadgeOriginBatch = z.infer<typeof AccessBadgeOriginBatchSchema>;
export const AccessBadgeOriginsSchema = z.object({
  bulks: z.array(AccessBadgeOriginBatchSchema),
  imports: z.array(AccessBadgeOriginBatchSchema),
});

// "Meus Selos" do aluno (GET /v1/me/access-badges; back#175, C3): todos os
// Selos dele nesta marca, inclusive vencidos (valid = false), por nome.
export const MyAccessBadgeSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  imageUrl: z.string().nullable(),
  expiresAt: z.string().nullable(),
  valid: z.boolean(),
});
export type MyAccessBadge = z.infer<typeof MyAccessBadgeSchema>;

// Atribuir a vários (back#174): o resultado de cada aluno, na ordem enviada.
export const AccessBadgeGrantManyResultSchema = z.object({
  items: z.array(
    z.object({ userId: z.string(), expiresAt: z.string().nullable(), renewed: z.boolean() }),
  ),
});
export const AccessBadgeGrantResultSchema = z.object({
  userId: z.string(),
  expiresAt: z.string().nullable(),
  renewed: z.boolean(),
});
